'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import styled from 'foundations-theming/utils';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { View } from 'foundations-components/transitional/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import DraggableFormFieldInput from 'FormsUI/components/editor/form/formFields/DraggableFormFieldInput';
import DependentFormFieldInput from 'FormsUI/components/editor/form/formFields/DependentFormFieldInput';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { getPropertyObjectTypeId } from 'FormsUI/utils/PropertiesUtils';
import { OPEN_DEPENDENT_FIELDS_EVENT } from 'FormsUI/constants/Events';
import { HIDE_DEPENDENT_FIELDS, SHOW_DEPENDENT_FIELDS } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { UpIcon } from 'foundations-assets/icons/components/UpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ArrowContainer = styled.div.withConfig({
  displayName: "FormField__ArrowContainer"
})(["text-align:center;line-height:0;"]);
const StyledDependentField = styled.div.withConfig({
  displayName: "FormField__StyledDependentField"
})(["width:80%;"]);
class FormField extends PureComponent {
  constructor() {
    super();
    this.state = {
      areDependentFieldsShown: false
    };
    this.handleShowDependentFieldsViaInlineButton = this.handleShowDependentFields.bind(this, 'inline-button');
    this.handleShowDependentFieldsViaFloatingButtonGroup = this.handleShowDependentFields.bind(this, 'floating-button-group');
    this.handleShowDependentFieldsViaNewDependentField = this.handleShowDependentFields.bind(this, 'new-dependent-file');
    this.handleOpenDependentFieldEvent = this.handleOpenDependentFieldEvent.bind(this);
  }
  componentDidMount() {
    window.addEventListener(OPEN_DEPENDENT_FIELDS_EVENT, this.handleOpenDependentFieldEvent);
  }
  componentWillUnmount() {
    window.removeEventListener(OPEN_DEPENDENT_FIELDS_EVENT, this.handleOpenDependentFieldEvent);
  }
  handleShowDependentFields(via) {
    this.setState({
      areDependentFieldsShown: !this.state.areDependentFieldsShown
    });
    if (!this.state.areDependentFieldsShown) {
      trackEditorInteraction(SHOW_DEPENDENT_FIELDS, {
        via
      });
    } else {
      trackEditorInteraction(HIDE_DEPENDENT_FIELDS, {
        via
      });
    }
  }
  handleOpenDependentFieldEvent(event) {
    const {
      areDependentFieldsShown
    } = this.state;
    if (event.detail === this.props.formField.name && !areDependentFieldsShown) {
      this.handleShowDependentFields();
    }
  }
  renderArrowIcon() {
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ArrowContainer, {
        children: /*#__PURE__*/_jsx(IconHolder, {
          icon: UpIcon,
          color: "border-interactive-pressed",
          verticalAlign: "bottom",
          padding: "0",
          marginBlockStart: "-200",
          size: "100"
        })
      })
    );
  }
  renderGrid() {
    return /*#__PURE__*/_jsxs(Grid, {
      children: [/*#__PURE__*/_jsx(GridItem, {
        size: 6,
        blockSize: "20px",
        borderStyle: "dashed",
        color: "border-interactive-pressed",
        borderInlineEndWidth: "100",
        borderInlineStartWidth: "0",
        borderBlockEndWidth: "0",
        borderBlockStartWidth: "0"
      }), /*#__PURE__*/_jsx(GridItem, {
        size: 6
      })]
    });
  }
  renderButton(formField) {
    const messageKey = this.state.areDependentFieldsShown ? 'editor.form.formFields.dependentFields.close' : 'editor.form.formFields.dependentFields.open';
    const options = this.state.areDependentFieldsShown ? {} : {
      numberDependentFields: formField.dependentFieldFilters.length
    };
    return /*#__PURE__*/_jsxs(View, {
      as: "div",
      marginBlockStart: "-200",
      children: [this.renderGrid(), /*#__PURE__*/_jsx(View, {
        as: "div",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          size: "small",
          onClick: this.handleShowDependentFieldsViaInlineButton,
          "data-test-id": "toggle-dependent-fields-list",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: messageKey,
            options: options
          })
        })
      })]
    });
  }
  renderDependentFields(formField) {
    const {
      formFieldGroupIndex,
      formFieldIndex,
      handleInputSelect,
      handleInputDelete,
      selectedFieldName,
      numberFormFieldsWithinGroup,
      handleDrag,
      removeDependentField
    } = this.props;
    const dependentFields = formField.dependentFieldFilters.map((dependentFieldFilter, dependentFieldFilterIndex) => {
      return /*#__PURE__*/_jsxs(StyledDependentField, {
        children: [this.renderGrid(), /*#__PURE__*/_jsx(DependentFormFieldInput, {
          formFieldGroupIndex: formFieldGroupIndex,
          dependentFieldFilter: dependentFieldFilter,
          dependentFieldFilterIndex: dependentFieldFilterIndex,
          numberFormFieldsWithinGroup: numberFormFieldsWithinGroup,
          formFieldIndex: formFieldIndex,
          formField: formField,
          handleInputSelect: handleInputSelect,
          handleInputDelete: handleInputDelete,
          selectedFieldName: selectedFieldName,
          handleDrag: handleDrag,
          removeDependentField: removeDependentField,
          isFormLabelPropertyEnabled: this.props.isFormLabelPropertyEnabled
        })]
      }, dependentFieldFilterIndex);
    });
    return /*#__PURE__*/_jsxs(TFlex, {
      justify: "center",
      align: "center",
      direction: "column",
      children: [this.renderArrowIcon(), dependentFields, this.renderButton()]
    });
  }
  renderDependentFieldsSection(formField) {
    if (formField.dependentFieldFilters.length === 0) {
      return null;
    }
    if (!this.state.areDependentFieldsShown) {
      return this.renderButton(formField);
    }
    return this.renderDependentFields(formField);
  }
  render() {
    const {
      formFieldGroupIndex,
      formFieldIndex,
      formField,
      handleInputSelect,
      handleInputDelete,
      selectedFieldName,
      numberFormFieldsWithinGroup,
      handleDrag,
      isQueuedField,
      groupHasRichText,
      updateFormField,
      formType,
      formFieldError,
      isUsedInRouting,
      isRemovableField,
      isDependentFieldUsedInRouting,
      removeRoutingFieldHandler
    } = this.props;
    const {
      areDependentFieldsShown
    } = this.state;
    const formFieldLabel = numberFormFieldsWithinGroup < 2 && groupHasRichText ? I18n.text('editor.form.formFields.labelAndRichText', {
      label: formField.label
    }) : formField.label;
    const propertyId = getPropertyObjectTypeId(formField);
    return /*#__PURE__*/_jsxs("div", {
      "data-test-id": `form-field-${propertyId}`,
      children: [/*#__PURE__*/_jsx(DraggableFormFieldInput, {
        id: `form-field-${propertyId}`,
        formFieldGroupIndex: formFieldGroupIndex,
        numberFormFieldsWithinGroup: numberFormFieldsWithinGroup,
        formFieldIndex: formFieldIndex,
        formField: formField,
        update: updateFormField,
        formFieldLabel: formFieldLabel,
        handleInputSelect: handleInputSelect,
        handleInputDelete: handleInputDelete,
        handleShowDependentFields: this.handleShowDependentFieldsViaFloatingButtonGroup,
        isRemovableField: isRemovableField,
        areDependentFieldsShown: areDependentFieldsShown,
        selectedFieldName: selectedFieldName,
        handleDrag: handleDrag,
        isQueuedField: isQueuedField,
        isFormLabelPropertyEnabled: this.props.isFormLabelPropertyEnabled,
        formType: formType,
        formFieldError: formFieldError,
        isUsedInRouting: isUsedInRouting,
        isDependentFieldUsedInRouting: isDependentFieldUsedInRouting,
        removeRoutingFieldHandler: removeRoutingFieldHandler
      }), this.renderDependentFieldsSection(formField)]
    });
  }
}
FormField.propTypes = {
  numberFormFieldsWithinGroup: PropTypes.number.isRequired,
  formFieldGroupIndex: PropTypes.number.isRequired,
  formFieldIndex: PropTypes.number.isRequired,
  formField: PropTypes.object.isRequired,
  updateFormField: PropTypes.func.isRequired,
  handleInputSelect: PropTypes.func.isRequired,
  handleInputDelete: PropTypes.func.isRequired,
  selectedFieldName: PropTypes.string,
  handleDrag: PropTypes.func.isRequired,
  removeDependentField: PropTypes.func.isRequired,
  isQueuedField: PropTypes.bool.isRequired,
  isRemovableField: PropTypes.bool.isRequired,
  groupHasRichText: PropTypes.bool,
  isUsedInRouting: PropTypes.bool.isRequired,
  isDependentFieldUsedInRouting: PropTypes.bool.isRequired,
  showHighlightAnimation: PropTypes.bool.isRequired,
  formType: PropTypes.string,
  isFormLabelPropertyEnabled: PropTypes.bool,
  formFieldError: PropTypes.object,
  removeRoutingFieldHandler: PropTypes.func.isRequired
};
FormField.defaultProps = {
  isQueuedField: false
};
export default FormField;